import { useEffect, useState } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useCopyToClipboard } from 'hooks/useCopyToClipboard';
import { JsonEditor, JsonEditorProps, monoLightTheme } from 'json-edit-react';
import { useInput } from 'react-admin';
import { Box } from 'shared/mui/Box';
import { IconButton } from 'shared/mui/IconButtons';
import { Tooltip } from 'shared/mui/Tooltip';

import { LabeledStyled } from './JsonInput.styled';

const jsonValueColor = 'rgba(25, 118, 210, 1)';

const theme: JsonEditorProps['theme'] = [
  monoLightTheme,
  {
    container: {
      backgroundColor: '#f6f6f6',
      fontFamily: 'Roboto, monospace',
      fontSize: '16px',
      borderRadius: '4px',
    },
    string: jsonValueColor,
    number: jsonValueColor,
    boolean: { color: jsonValueColor, fontStyle: 'normal' },
    null: { color: jsonValueColor, fontVariant: 'normal', fontWeight: 'normal' },
    iconCollection: 'rgb(0, 43, 54)',
    iconEdit: 'edit',
    iconDelete: 'rgb(203, 75, 22)',
    iconAdd: 'green',
    iconCopy: 'rgb(38, 139, 210)',
    iconOk: 'green',
    iconCancel: 'rgb(203, 75, 22)',
  },
];

export const JsonInput = (props: any): JSX.Element => {
  const { field, isRequired } = useInput(props);
  const [data, setData] = useState({});

  useEffect(() => {
    if (field.value) {
      setData(field.value);
    }
  }, []);

  const { tooltipText, handleCopy } = useCopyToClipboard(JSON.stringify(data, null, 2));

  const isEmptyData = !Object.keys(data).length;

  return (
    <LabeledStyled {...props} isRequired={isRequired}>
      <Box sx={{ position: 'relative', height: '100%' }}>
        <JsonEditor
          data={data}
          onUpdate={({ newData }) => {
            setData(newData);
            field.onChange(newData);
          }}
          stringTruncate={1000000}
          theme={theme}
        />
        <Tooltip
          PopperProps={{
            disablePortal: true,
          }}
          open={!!tooltipText}
          sx={{ position: 'absolute', top: '6px', right: '6px' }}
          title={tooltipText}
        >
          <IconButton disabled={isEmptyData} onClick={handleCopy}>
            <ContentCopyIcon color={isEmptyData ? 'secondary' : 'primary'} />
          </IconButton>
        </Tooltip>
      </Box>
    </LabeledStyled>
  );
};
